import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { RcListView, firstUnfinishedPage, recallById } from '@elva/ui';
import { useClearPageTopBarActions } from '@/app/layouts/pageTopBar';
import { useRecallHold } from '@/features/ai-team/hooks/useRecallHold';
import { loadDraftFor } from './RecallBuilderPages';

/**
 * The recall list (amendment 22). **Every control goes somewhere** — before this,
 * the toggle, Duplicate and row clicks were inert, every Report showed Hygiene,
 * Setup opened the old sequence page and Settings opened General settings.
 */
export function RecallListPage() {
  useClearPageTopBarActions();
  const navigate = useNavigate();
  // The same flag the agent page sets (amendment 24 §6) — this banner was a prop
  // nothing ever passed, so holding the agent left `/recall` looking normal.
  const { onHold, setHold } = useRecallHold();

  /** A draft resumes at the first page that still has something to fix, rather
   * than making somebody click through work they have already done. */
  const continueAt = (id: string) => {
    void navigate(`/recall/${id}/${firstUnfinishedPage(loadDraftFor(id))}`);
  };

  return (
    <RcListView
      onHold={onHold}
      onResume={() => { setHold(false); }}
      onNew={() => { void navigate('/recall/new/audience'); }}
      onOpenSettings={() => { void navigate('/settings/ai/recall'); }}
      onReport={(id) => { void navigate(`/recall/${id}/report`); }}
      onEdit={(id) => { void navigate(`/recall/${id}/audience`); }}
      onContinue={continueAt}
      onDuplicated={(id) => { void navigate(`/recall/${id}/audience`); }}
    />
  );
}

/**
 * `/recall/<id>` on its own (§4): a running recall opens its report, a draft
 * resumes in the builder. The old sequence page that used to live here is gone.
 */
export function RecallResolvePage() {
  const { id } = useParams<{ id: string }>();
  const row = id === undefined ? undefined : recallById(id);
  if (id === undefined || row === undefined) return <Navigate to="/recall" replace />;
  const to =
    row.state === 'draft' ? `/recall/${id}/${firstUnfinishedPage(loadDraftFor(id))}` : `/recall/${id}/report`;
  return <Navigate to={to} replace />;
}
